<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { getStackDepth, getVisibleCollapsedItems } from '@/domains/notifications/flashbar';
import { getNoticeIcon, type NoticeType } from '@/domains/notifications/notice-icons';
import NotificationCard from '@/domains/notifications/NotificationCard.vue';
import { ArrowDown } from '@element-plus/icons-vue';
import { useElementBounding, useMagicKeys, useScrollLock, whenever } from '@vueuse/core';

import type { FormattedNotifyMsg } from '@/domains/notifications/queries';

// Collapsed peek geometry (per layer behind the top):
//   translateY +14, scale -0.015. Opacity stays at 1 — fading deeper cards
//   would let their content text show through the card in front (multiple
//   semi-transparent rectangles stacked combine into visible bleed-through).
const STACK_Y_STEP = 14;
const STACK_SCALE_STEP = 0.015;
const EXPANDED_GAP = 10;
const PILL_HALF_HEIGHT = 24; // ≈ half of the rendered pill, used to straddle
// Vertical breathing room below the collapsed deck — reserves space for the
// pill's lower portion that sticks out past the deck bottom. Hand-tuned.
const PILL_BOTTOM_RESERVE = 18;
type CardPlacement = { y: number; scale: number; opacity: number };

// Every expand/collapse transition in this component runs at this duration —
// keep it in step with the `duration-200` utilities in the template.
const TRANSITION_MS = 200;
// Vue applies the backdrop's leave-to class a frame or two after `expanded`
// flips, so the fade ends that much later than a bare TRANSITION_MS timer
// would. The buffer covers that scheduling gap; without it the tier can drop
// while the backdrop is still painting.
const OVERLAY_LEAVE_MS = TRANSITION_MS + 60;

// Per-type icon colour — data-driven via :style so the pill icons land on
// the same accent palette as NotificationCard's left border (single source
// of truth) without a separate set of scoped classes.
const TYPE_ICON_COLOR: Record<NoticeType, string> = {
  info: 'var(--cd-info)',
  warning: 'var(--cd-brand-tangerine)',
  success: 'var(--cd-success)',
  error: 'var(--cd-danger)',
};

const { items, pendingId } = defineProps<{
  items: FormattedNotifyMsg[];
  pendingId: string | null;
}>();

const emit = defineEmits<{
  'mark-read': [item: FormattedNotifyMsg];
  jump: [item: FormattedNotifyMsg];
}>();

const { t } = useI18n();

const expanded = ref(false);
const isCollapsible = computed(() => items.length > 1);

// The scroll layer must hold its high z tier until the collapse transition
// finishes. Explicit z-index (backdrop 2100) beats z-auto regardless of DOM
// order, so dropping the tier the instant `expanded` flips lets the fading
// backdrop paint over the cards still sliding back to the deck.
const overlayActive = ref(false);
let overlayLeaveTimer: ReturnType<typeof setTimeout> | null = null;
watch(expanded, (v) => {
  if (overlayLeaveTimer) {
    clearTimeout(overlayLeaveTimer);
    overlayLeaveTimer = null;
  }
  if (v) {
    overlayActive.value = true;
    return;
  }
  overlayLeaveTimer = setTimeout(() => {
    overlayActive.value = false;
    overlayLeaveTimer = null;
  }, OVERLAY_LEAVE_MS);
});

// Each card measures its own rendered height so the expanded list can use
// the REAL height per row (not a single measured top-card height).
// Otherwise 4-line cards overlap because expanded translateY assumes
// single-line height.
//
// Keyed by `itemKey(item)` (biz_id / detail_biz_id, matching the template
// :key) rather than array index. When Vue reuses a card's DOM node after
// a sibling is dismissed, the item's position shifts but its key stays
// with the node — so a ResizeObserver captured at attach time keeps
// writing to the right slot. Index-keyed storage produced the "second
// card inherits the first card's height" bug when the first was removed.
const cardHeights = ref<Record<string, number>>({});
const observers = new Map<string, ResizeObserver>();

function detach(key: string): void {
  const ro = observers.get(key);
  if (!ro) return;
  ro.disconnect();
  observers.delete(key);
  if (key in cardHeights.value) {
    const { [key]: _dropped, ...rest } = cardHeights.value;
    cardHeights.value = rest;
  }
}

function attach(key: string, el: HTMLElement): void {
  if (observers.has(key)) return;
  const ro = new ResizeObserver(() => {
    // Border-box height (incl. the card's own padding), NOT contentRect —
    // this value is fed to the outer clip's `height`, so it must match the
    // card's full rendered height or the clip crops the padding and the
    // content overflows/overlaps.
    const h = el.offsetHeight;
    // Discard the height=0 pulse that fires the moment an exiting card's
    // DOM detaches. Writing 0 to the top card's slot cratered
    // topCardHeight, which dragged root min-height from ~92px down to
    // 32px — the page below jumped ~60px in the wrong direction during
    // card removal.
    if (h <= 0) return;
    if (cardHeights.value[key] !== h) {
      cardHeights.value = { ...cardHeights.value, [key]: h };
    }
  });
  ro.observe(el);
  observers.set(key, ro);
}

function setCardEl(item: FormattedNotifyMsg, index: number) {
  const key = itemKey(item, index);
  return (node: unknown): void => {
    const el = (node as { $el?: HTMLElement } | null)?.$el ?? (node as HTMLElement | null);
    if (!el) {
      detach(key);
      return;
    }
    attach(key, el);
  };
}

onBeforeUnmount(() => {
  observers.forEach((ro) => ro.disconnect());
  observers.clear();
});

function heightOf(item: FormattedNotifyMsg, index: number): number | undefined {
  return cardHeights.value[itemKey(item, index)];
}

// Top card height drives collapsed deck geometry. Falls back to a sensible
// default (60) until the first ResizeObserver tick lands.
const topCardHeight = computed<number>(() => (items.length ? (heightOf(items[0], 0) ?? 60) : 60));

const stackDepth = computed(() => getStackDepth(items));
const collapsedDeckIndex = computed(() => {
  const map = new Map<FormattedNotifyMsg, number>();
  getVisibleCollapsedItems(items, stackDepth.value).forEach((item, i) => map.set(item, i));
  return map;
});
// -1 = not in the collapsed deck; 0 = top card; >0 = peek layer.
function deckIndexOf(index: number): number {
  return collapsedDeckIndex.value.get(items[index]!) ?? -1;
}

const visiblePeeks = computed(() => Math.max(stackDepth.value - 1, 0));
const collapsedDeckHeight = computed<number>(
  () => topCardHeight.value + visiblePeeks.value * STACK_Y_STEP,
);

// The root only reserves the COLLAPSED deck height. Freeze it while expanded:
// the expanded list is a fixed overlay, so letting the (now full-height) top
// card grow the root would reflow the whole page below it every frame.
//
// `expanded` is a watch source, not just a guard: cards dismissed while
// expanded change the deck height at a moment this watch is skipping, and
// without re-reading on the way back down the reserve stays stuck on the
// pre-dismissal value.
const reservedDeckHeight = ref(0);
watch(
  [collapsedDeckHeight, expanded],
  ([h, isExpanded]) => {
    if (!isExpanded) reservedDeckHeight.value = h;
  },
  { immediate: true },
);

// Cumulative offset for each card in the expanded vertical list — based on
// each card's individual measured height plus the gap between cards. This is
// what fixes the "expanded cards overlap" bug: a 4-line card and a 1-line
// card have very different heights, and the next card's translateY needs to
// reflect that.
function expandedYFor(index: number): number {
  let y = 0;
  for (let i = 0; i < index; i++) {
    y += (heightOf(items[i], i) ?? topCardHeight.value) + EXPANDED_GAP;
  }
  return y;
}

function cardBaseState(index: number): Omit<CardPlacement, 'opacity'> {
  if (expanded.value) {
    return { y: expandedYFor(index), scale: 1 };
  }
  const deckIdx = deckIndexOf(index);
  if (deckIdx < 0) {
    // Not in the collapsed deck: tuck behind the deepest peek at opacity 0
    // so it keeps its DOM slot (and its measured height) while hidden.
    const deepest = Math.max(stackDepth.value - 1, 0);
    return { y: deepest * STACK_Y_STEP, scale: 1 - deepest * STACK_SCALE_STEP };
  }
  return { y: deckIdx * STACK_Y_STEP, scale: 1 - deckIdx * STACK_SCALE_STEP };
}

function cardPlacement(index: number): CardPlacement {
  const base = cardBaseState(index);
  return {
    ...base,
    opacity: !expanded.value && deckIndexOf(index) < 0 ? 0 : 1,
  };
}

// Clip height = the card's CURRENT measured content height (single-line
// collapsed, full expanded).
function cardClipHeight(index: number): string {
  return `${heightOf(items[index], index) ?? topCardHeight.value}px`;
}

function cardZIndex(index: number): number {
  return Math.max(items.length - index, 1);
}

// Cards are absolutely positioned and placed purely by transform, so this
// style IS the layout — `translateY` before `scale` keeps the offset
// unscaled.
function cardStyle(index: number): Record<string, string | number> {
  const { y, scale, opacity } = cardPlacement(index);
  return {
    zIndex: expanded.value ? 2110 + (items.length - index) : cardZIndex(index),
    height: cardClipHeight(index),
    transform: `translateY(${y}px) scale(${scale})`,
    opacity,
  };
}

function itemKey(item: FormattedNotifyMsg, index: number): string {
  return item.detail_biz_id || item.biz_id || String(index);
}

const countByType = computed<{ type: NoticeType; count: number }[]>(() => {
  const order: NoticeType[] = ['error', 'warning', 'success', 'info'];
  const counts: Record<NoticeType, number> = { error: 0, warning: 0, success: 0, info: 0 };
  for (const item of items) counts[item.msg_type] += 1;
  return order.map((type) => ({ type, count: counts[type] }));
});

// ===== Pill positioning =====
// Pill lives inside the flashbar root, so its anchor top is relative to the
// root (no viewport-coordinate math needed).

// Cumulative bottom Y of the expanded list (sum of all card heights + gaps).
const expandedListHeight = computed<number>(() => {
  if (!items.length) return 0;
  let total = 0;
  for (let i = 0; i < items.length; i++) {
    total += heightOf(items[i], i) ?? topCardHeight.value;
  }
  total += (items.length - 1) * EXPANDED_GAP;
  return total;
});

// Pill anchor's `top` is relative to the flashbar root now (pill lives inside
// the root, not teleported to body). The flashbarTop offset is no longer in
// the formula.
const pillAnchorTopPx = computed<number>(() => {
  if (expanded.value) {
    return expandedListHeight.value + EXPANDED_GAP;
  }
  return collapsedDeckHeight.value - PILL_HALF_HEIGHT;
});

// Modal-style scroll: lock the page when expanded, the scroll layer below
// owns its own overflow-y so users scroll the list inside the overlay
// without dragging the page underneath.
const bodyScrollLocked = useScrollLock(document.body);
watch(expanded, (v) => {
  bodyScrollLocked.value = v;
});
// Collapse whenever the stack stops being collapsible — either the list
// drained to zero (root `v-if` hides but the component stays mounted,
// leaking scroll lock) or the second-to-last card was dismissed while
// expanded (pill is gone because isCollapsible=false, leaving the user
// scroll-locked around a single card with no visible way out).
watch(isCollapsible, (v) => {
  if (!v) expanded.value = false;
});
onBeforeUnmount(() => {
  bodyScrollLocked.value = false;
  if (overlayLeaveTimer) {
    clearTimeout(overlayLeaveTimer);
    overlayLeaveTimer = null;
  }
});

// Track flashbar root's screen position so the expanded scroll layer can
// be `position: fixed` but pinned to the SAME left/width as the collapsed
// state. Without this, switching from `absolute inset-0` (root-bound) to
// `fixed inset-0` (viewport-bound) would stretch cards to the viewport edge.
const flashbarRoot = useTemplateRef<HTMLElement>('flashbarRoot');
const { top: rootTop, left: rootLeft, width: rootWidth } = useElementBounding(flashbarRoot);

const scrollLayerStyle = computed(() => {
  if (!overlayActive.value) return undefined;
  return {
    position: 'fixed' as const,
    top: `${rootTop.value}px`,
    left: `${rootLeft.value}px`,
    width: `${rootWidth.value}px`,
    bottom: '0',
  };
});

const keys = useMagicKeys();
whenever(
  computed(() => expanded.value && keys.escape.value),
  () => {
    expanded.value = false;
  },
);

function togglePill(): void {
  if (!isCollapsible.value) return;
  expanded.value = !expanded.value;
}

function onBackdropClick(): void {
  expanded.value = false;
}
</script>

<template>
  <!-- Root reserves the collapsed deck's height so the page content below
       doesn't sit under the cards. Transitioned so dismissing a card doesn't
       snap that content upward. -->
  <div
    v-if="items.length"
    ref="flashbarRoot"
    class="relative w-full transition-[min-height] duration-200 ease-out"
    :style="{
      minHeight: `${reservedDeckHeight + (isCollapsible ? PILL_BOTTOM_RESERVE : 0)}px`,
    }"
    data-testid="notification-flashbar"
  >
    <!-- Backdrop: full-viewport visual veil + click-to-close. The scroll
         layer below only covers the content-area column, so the area to
         either side (sidebar gutter, padding) belongs to the backdrop.
         z-tier is [2100..2120] — the wujie sub's shadow DOM shares the
         host document's root stacking context (the `<wujie-app>` element
         creates no isolation), so Element UI tooltips / loading masks /
         dropdowns inside legacy render at 2000+ in the same context.
         Anything below 2100 gets punched through. Long-term fix is a
         real z-tier design in the shared style layer; tracked
         separately (see z_index_strategy_missing memory). -->
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-200 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="expanded"
        class="fixed inset-0 z-2100"
        style="background: var(--el-overlay-color-lighter)"
        data-testid="notification-flashbar-backdrop"
        @click="onBackdropClick"
      />
    </Transition>

    <!-- Scroll layer holds cards + pill.
         - Collapsed: `absolute inset-0` of the flashbar root.
         - Expanded: `position: fixed` pinned to the root's screen rect
           (top/left/width) extending to viewport bottom, so cards keep the
           same x/width across the toggle. -->
    <!-- Native overflow-y, NOT ElScrollbar (which the rest of v3 chrome uses):
         ElScrollbar's `.el-scrollbar__wrap` + `.el-scrollbar__view` layers
         each carry their own overflow rules, which clip the absolutely
         positioned cards. Modal-style overlays in EP (ElDialog body) face the
         same constraint. -->
    <!-- Only carry the high z tier while the overlay is up — collapsed the
         flashbar is just in-flow chrome and must NOT hover above legacy
         dialogs (Element UI 2.x PopupManager starts at 2000). -->
    <div
      :class="[overlayActive ? 'z-2110 overflow-y-auto overscroll-contain' : 'absolute inset-0']"
      :style="scrollLayerStyle"
      data-testid="notification-flashbar-scroll"
      @click.self="expanded && onBackdropClick()"
    >
      <!-- Cards/pill share this wrapper so both are positioned against the
           same origin in either state. While the overlay is up the wrapper
           needs an explicit minHeight — cards/pill are absolute and
           contribute 0 to layout — otherwise overflow-y on the scroll layer
           has nothing to scroll when the backlog spills past the viewport. -->
      <div
        class="relative w-full"
        :style="overlayActive ? { minHeight: `${pillAnchorTopPx + 64}px` } : undefined"
      >
        <!-- `height` is transitioned only while expanding: on collapse it has
             to snap in step with the card's own single-line content swap
             (`:compact`), or the box outruns its content.

             `:compact` is gated on `isCollapsible` because a lone notice
             renders no pill — compacting it would strand the user on one
             truncated line with no way to expand. -->
        <div
          v-for="(item, index) in items"
          :key="itemKey(item, index)"
          class="absolute top-0 right-0 left-0 overflow-hidden rounded-[4px] shadow-[0_6px_18px_rgba(0,0,0,0.08)] duration-200 ease-out"
          :class="[
            expanded ? 'transition-[transform,opacity,height]' : 'transition-[transform,opacity]',
            { 'pointer-events-none': !expanded && deckIndexOf(index) !== 0 },
          ]"
          :style="cardStyle(index)"
          :aria-hidden="!expanded && deckIndexOf(index) !== 0"
          :inert="(!expanded && deckIndexOf(index) !== 0) || undefined"
          :data-deck-index="expanded ? undefined : deckIndexOf(index)"
          data-testid="notification-flashbar-card"
        >
          <NotificationCard
            :ref="setCardEl(item, index)"
            :item="item"
            :pending="pendingId === item.detail_biz_id"
            :compact="!expanded && isCollapsible"
            @mark-read="(it) => emit('mark-read', it)"
            @jump="(it) => emit('jump', it)"
          />
        </div>

        <div
          v-if="isCollapsible"
          class="absolute top-0 left-1/2 transition-transform duration-200 ease-out"
          :class="overlayActive ? 'z-2120' : 'z-20'"
          :style="{ transform: `translateY(${pillAnchorTopPx}px)` }"
        >
          <div
            class="bg-surface hover:bg-surface-elevated border-line text-fg inline-flex -translate-x-1/2 cursor-pointer items-center gap-4.5 rounded-full border px-5.5 py-2.25 text-[13px] leading-none whitespace-nowrap shadow-[0_6px_16px_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.06)] transition-colors duration-150 select-none"
            role="button"
            tabindex="0"
            :aria-expanded="expanded"
            :aria-label="t('notification.title')"
            data-testid="notification-flashbar-pill"
            @click="togglePill"
            @keydown.enter.prevent="togglePill"
            @keydown.space.prevent="togglePill"
          >
            <span class="leading-none font-semibold">{{ t('notification.title') }}</span>
            <span class="inline-flex items-center gap-3.5 leading-none">
              <span
                v-for="c in countByType"
                :key="c.type"
                class="inline-flex items-center gap-1.5 leading-none"
              >
                <ElIcon :size="16" :style="{ color: TYPE_ICON_COLOR[c.type] }">
                  <component :is="getNoticeIcon(c.type)" />
                </ElIcon>
                <span class="text-fg-3">{{ c.count }}</span>
              </span>
            </span>
            <div
              class="text-fg-3 inline-flex items-center transition-[rotate] duration-200 ease-out"
              :class="{ 'rotate-180': expanded }"
            >
              <ElIcon :size="16">
                <ArrowDown />
              </ElIcon>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
